import React from 'react';
import type { FC, ReactNode } from 'react';

import type { SupportedLanguage } from '@modernloop/i18n';
import { SupportedLocalesContext } from '@modernloop/shared/feature-flag';

import { toSupportedLanguages } from 'src/contexts/supportedLocales';

import { useSupportedLocales } from 'src/hooks/api/org/useSupportedLocales';

interface SupportedLocalesProviderProps {
  children?: ReactNode;
}

/**
 * Reads the org's enabled locales (`thisOrg.supportedLocales`) on the recruiting schema and makes
 * them available via SupportedLocalesContext.
 *
 * The backend (`OrgClient.getSupportedLocales`) already normalizes the list and always includes
 * the org default, so the frontend just maps enum → i18n code. Recruiting / main app only — the
 * interviewer portal uses InterviewerSupportedLocalesProvider, which runs the equivalent query
 * against the interviewer schema/client.
 */
export const SupportedLocalesProvider: FC<SupportedLocalesProviderProps> = ({ children }) => {
  const { data } = useSupportedLocales();

  const value: SupportedLanguage[] = React.useMemo(() => toSupportedLanguages(data?.thisOrg?.supportedLocales), [data]);

  return <SupportedLocalesContext.Provider value={value}>{children}</SupportedLocalesContext.Provider>;
};
